import React from 'react';
import { useRecoilValue } from 'recoil';
import { blockDisplayHeader, blockSettings, customizedBlock } from 'venn-state';
import { Block } from '../../components/core';
import { DefaultSubjects } from '../../components/secondary/subjects/default-subjects';
import { AsOfDate } from '../../components/secondary/as-of-date';
import { type HoldingsBreakdownType, useHoldings } from '../../../studio-blocks';
import {
  assert,
  assertExhaustive,
  assertNotNil,
  type CustomBlockTypeEnum,
  getDateBehavior,
  isHoldingsBreakdownBlock,
} from 'venn-utils';
import { HoldingsCalculationInfo } from '../../components/secondary/holdings-calculation-info';
import { BenchmarkType } from '../../components/secondary/benchmark-type';

interface HoldingsHeaderProps {
  id: string;
}

export const getBreakdownType = (blockType: CustomBlockTypeEnum): HoldingsBreakdownType => {
  assert(isHoldingsBreakdownBlock(blockType));
  switch (blockType) {
    case 'GEOGRAPHY_EXPOSURE':
    case 'SECTOR_EXPOSURE':
    case 'CREDIT_BREAKDOWN':
    case 'MATURITY_BREAKDOWN':
    case 'DURATION_BREAKDOWN':
    case 'MUNI_BREAKDOWN':
      return 'Long Rescaled Values';
    case 'ASSET_EXPOSURE':
      return 'Net Rescaled Values';
    default:
      return assertExhaustive(blockType);
  }
};

export const HoldingsHeader = ({ id }: HoldingsHeaderProps) => {
  const { subHeader } = useRecoilValue(customizedBlock(id));
  const title = useRecoilValue(blockDisplayHeader(id));
  const { data } = useHoldings(id);
  const holdingsData = assertNotNil(data);
  const blockSetting = useRecoilValue(blockSettings(id));
  const dateBehavior = getDateBehavior(blockSetting.customBlockType);
  const blockType = blockSetting?.customBlockType;

  return (
    <>
      <Block.HeaderTopContainer id={id}>
        <Block.Title>
          <Block.Headline id={id}>{title}</Block.Headline>
          {subHeader && <Block.SubHeadline id={id}>{subHeader}</Block.SubHeadline>}
        </Block.Title>
        <Block.Metadata id={id}>
          <BenchmarkType id={id} />
          <AsOfDate holdingsData={holdingsData} dateBehavior={dateBehavior} />
          <HoldingsCalculationInfo breakdownType={getBreakdownType(blockType)} />
        </Block.Metadata>
      </Block.HeaderTopContainer>

      <DefaultSubjects id={id} relativeToBenchmark={false} />
    </>
  );
};
